import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import dayjs from "dayjs";
import "./InternationalVisitors.css";
import { useLocation } from "react-router-dom";
import passportSampleImg from "../../../Assets/passport_sample.png";
import { useFormContext } from "../../../context/Form_context";
import { useBasicContext } from "../../../context/Basic_context";
import Notification from "../../../utils/Notification";
import Loader from "../../../Common/loader/Loader";
import {
  FaUser,
  FaBuilding,
  FaEnvelope,
  FaWhatsapp,
  FaBriefcase,
  FaGlobe,
  FaCircleCheck,
  FaLocationDot,
  FaCity,
  FaShieldHalved,
  FaGem,
  FaHeadset,
  FaTicket,
  FaCalendarDays,
  FaFileContract,
  FaArrowRight,
  FaChevronDown,
} from "react-icons/fa6";
import { DynamicIcon } from "../../../utils/helper";



const isFieldEnabled = (format, key) => {
  if (!format) return false;
  const val = format[key];
  return val === "1" || val === 1 || val === true || val === "true";
};

const getCleanOptions = (optionsArray) => {
  if (!optionsArray || !Array.isArray(optionsArray)) return [];
  return optionsArray
    .map((opt) =>
      typeof opt === "string"
        ? opt.trim()
        : opt?.option || opt?.name || String(opt).trim()
    )
    .filter((opt) => Boolean(opt) && opt !== "");
};

const isChildRequired = (child) => {
  if (!child) return false;
  return String(child.is_required) === "1" || child.is_required === 1;
};

const getChildOptions = (child) => {
  if (
    child?.visitor_format_child_option &&
    Array.isArray(child.visitor_format_child_option)
  ) {
    return child.visitor_format_child_option
      .filter((item) => {
        if (typeof item === "object" && item !== null) {
          const reqVal = item?.is_text_required ?? item?.is_required ?? item?.status;
          if (reqVal !== undefined && reqVal !== null) {
            return String(reqVal) === "1" || reqVal === 1 || reqVal === true || reqVal === "true";
          }
        }
        return true;
      })
      .map((item) => (typeof item === "object" ? item?.option || item?.name : item))
      .filter((opt) => Boolean(opt) && String(opt).trim() !== "");
  }
  return [];
};

const getFieldIcon = (label) => {
  const l = (label || "").toLowerCase();
  if (
    l.includes("website") ||
    l.includes("url") ||
    l.includes("link") ||
    l.includes("site")
  ) {
    return <FaGlobe className="intl-field-icon" />;
  }
  if (
    l.includes("address") ||
    l.includes("location") ||
    l.includes("street")
  ) {
    return <FaLocationDot className="intl-field-icon" />;
  }
  if (
    l.includes("phone") ||
    l.includes("whatsapp") ||
    l.includes("mobile") ||
    l.includes("contact")
  ) {
    return <FaWhatsapp className="intl-field-icon intl-whatsapp-icon" />;
  }
  if (l.includes("email") || l.includes("mail")) {
    return <FaEnvelope className="intl-field-icon" />;
  }
  if (
    l.includes("company") ||
    l.includes("organization") ||
    l.includes("firm")
  ) {
    return <FaBuilding className="intl-field-icon" />;
  }
  if (
    l.includes("sector") ||
    l.includes("industry") ||
    l.includes("business") ||
    l.includes("category")
  ) {
    return <FaBriefcase className="intl-field-icon" />;
  }
  if (
    l.includes("date") ||
    l.includes("dob") ||
    l.includes("birth") ||
    l.includes("calendar")
  ) {
    return <FaCalendarDays className="intl-field-icon" />;
  }
  return <FaUser className="intl-field-icon" />;
};

const InternationalVisitors = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryId = searchParams.get("id");
  const stateId = location.state?.categoryId || location.state?.id;

  const {
    GetRegisterDetailExpo,
    GetRegisterForm,
    GetVisitorFormat,
    PostInternationalForm,
    register_form_data,
    register_form_loading,
    register_detail_expo_data,
    register_detail_expo_loading,
    visitor_format_data,
    visitor_format_loading,
    submit_international_visitor_loading,
  } = useFormContext();

  const {
    GetCountry,
    get_country_data,
    get_country_loading,
    GetState,
    get_state_data,
    get_state_loading,
    GetCity,
    get_city_data,
    get_city_loading,
  } = useBasicContext();

  useEffect(() => {
    GetVisitorFormat("2");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeFormat = useMemo(() => {
    return visitor_format_data || {};
  }, [visitor_format_data]);

  useEffect(() => {
    if (
      isFieldEnabled(activeFormat, "country") ||
      isFieldEnabled(activeFormat, "country_code") ||
      isFieldEnabled(activeFormat, "state") ||
      isFieldEnabled(activeFormat, "city")
    ) {
      GetCountry();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFormat]);

  useEffect(() => {
    const activeId = queryId || stateId;
    const isFormLoaded =
      register_form_data && Object.keys(register_form_data).length > 0;

    if (activeId) {
      GetRegisterDetailExpo(activeId);
    } else if (register_form_data?.registration_categories?.length > 0) {
      const categories = register_form_data.registration_categories;
      const matched = categories.find(
        (c) =>
          c.name?.toLowerCase().includes("international") ||
          c.tag?.toLowerCase().includes("international") ||
          c.name?.toLowerCase().includes("overseas") ||
          c.name?.toLowerCase().includes("foreign"),
      );

      if (matched?.id) {
        GetRegisterDetailExpo(matched.id);
      }
    } else if (!isFormLoaded && !register_form_loading) {
      GetRegisterForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryId, stateId, register_form_data]);

  const formattedDates = useMemo(() => {
    const fromStr = register_detail_expo_data?.from_date;
    const toStr = register_detail_expo_data?.to_date;
    if (!fromStr || !toStr) return "";

    const fromDate = dayjs(fromStr);
    const toDate = dayjs(toStr);

    if (!fromDate.isValid() || !toDate.isValid()) return "";

    if (
      fromDate.month() === toDate.month() &&
      fromDate.year() === toDate.year()
    ) {
      return `${fromDate.format("D")} – ${toDate.format("D MMMM YYYY")}`;
    } else {
      return `${fromDate.format("D MMM")} – ${toDate.format("D MMM YYYY")}`;
    }
  }, [
    register_detail_expo_data?.from_date,
    register_detail_expo_data?.to_date,
  ]);

  const [formData, setFormData] = useState({
    title: "Mr.",
    countryCode: "+91",
  });
  const [selectedProductCategories, setSelectedProductCategories] = useState([]);

  const handleCategoryToggle = (categoryName) => {
    setSelectedProductCategories((prev) => {
      if (prev.includes(categoryName)) {
        return prev.filter((item) => item !== categoryName);
      } else {
        return [...prev, categoryName];
      }
    });
  };

  const [formKey, setFormKey] = useState(0);

  const currentCountryObj = useMemo(() => {
    if (!formData.countryCode || !Array.isArray(get_country_data)) return null;
    const targetCode = formData.countryCode.trim();
    return get_country_data.find((c) => {
      const rawCode =
        c?.phonecode ||
        c?.phone_code ||
        c?.country_code ||
        c?.isd_code ||
        c?.code ||
        "";
      const formattedCode = String(rawCode).startsWith("+")
        ? String(rawCode)
        : `+${rawCode}`;
      return formattedCode === targetCode;
    });
  }, [formData.countryCode, get_country_data]);

  const selectedSortName = useMemo(() => {
    const sortName =
      currentCountryObj?.sortname ||
      currentCountryObj?.sort_name ||
      currentCountryObj?.iso2 ||
      currentCountryObj?.iso_code ||
      currentCountryObj?.short_name ||
      (formData.countryCode === "+91" ? "IN" : "");
    return sortName ? sortName.toLowerCase() : "";
  }, [currentCountryObj, formData.countryCode]);

  useEffect(() => {
    if (formData.country) {
      const matchedCountry = Array.isArray(get_country_data)
        ? get_country_data.find(
            (c) =>
              c?.name === formData.country ||
              c?.country_name === formData.country ||
              String(c?.id) === String(formData.country)
          )
        : null;
      const cId = matchedCountry?.id || formData.country;
      if (cId) {
        GetState(cId);
      }
    } else if (
      isFieldEnabled(activeFormat, "state") &&
      !isFieldEnabled(activeFormat, "country")
    ) {
      GetState(101);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.country, activeFormat]);

  useEffect(() => {
    if (formData.state) {
      GetCity(formData.state);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.state]);

  const resetForm = () => {
    setFormData({ title: "Mr.", countryCode: "+91" });
    setSelectedProductCategories([]);
    setFormKey((prev) => prev + 1);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
      if (name === "country") {
        updated.state = "";
        updated.city = "";
      }
      if (name === "state") {
        updated.city = "";
      }
      if (name === "country" && value && Array.isArray(get_country_data)) {
        const matchedCountry = get_country_data.find(
          (c) =>
            c?.name === value ||
            c?.country_name === value ||
            String(c?.id) === String(value)
        );
        const rawCode =
          matchedCountry?.phonecode ||
          matchedCountry?.phone_code ||
          matchedCountry?.country_code ||
          matchedCountry?.isd_code ||
          matchedCountry?.code;
        if (rawCode) {
          updated.countryCode = String(rawCode).startsWith("+")
            ? String(rawCode)
            : `+${rawCode}`;
        }
      }
      return updated;
    });
  };

  const validate = () => {
    const errors = {};

    if (isFieldEnabled(activeFormat, "title") && !formData.title) {
      errors.title = "Select Title";
    }
    if (
      isFieldEnabled(activeFormat, "first_name") &&
      !formData.firstName?.trim()
    ) {
      errors.firstName = "Enter First Name";
    }
    if (
      isFieldEnabled(activeFormat, "last_name") &&
      !formData.lastName?.trim()
    ) {
      errors.lastName = "Enter Last Name";
    }
    if (isFieldEnabled(activeFormat, "country") && !formData.country) {
      errors.country = "Select Country";
    }
    if (
      (isFieldEnabled(activeFormat, "contact") ||
        isFieldEnabled(activeFormat, "contact_no") ||
        isFieldEnabled(activeFormat, "mobile") ||
        isFieldEnabled(activeFormat, "whatsapp")) &&
      !formData.mobile?.trim() &&
      !formData.contact?.trim()
    ) {
      errors.mobile = "Enter Mobile Number";
    }
    if (isFieldEnabled(activeFormat, "email")) {
      const emailVal = formData.email?.trim();
      if (!emailVal) {
        errors.email = "Enter Email Address";
      } else if (!/\S+@\S+\.\S+/.test(emailVal)) {
        errors.email = "Enter valid Email Address";
      }
    }
    if (
      (isFieldEnabled(activeFormat, "company") ||
        isFieldEnabled(activeFormat, "company_name")) &&
      !formData.companyName?.trim()
    ) {
      errors.companyName = "Enter Company Name";
    }
    if (
      (isFieldEnabled(activeFormat, "designation") ||
        isFieldEnabled(activeFormat, "designation_name")) &&
      !formData.designation?.trim()
    ) {
      errors.designation = "Enter Designation";
    }
    if (
      isFieldEnabled(activeFormat, "product_category") &&
      selectedProductCategories.length === 0
    ) {
      errors.selectedProductCategories = "Select at least one Product Category";
    }
    if (
      (isFieldEnabled(activeFormat, "business_nature") ||
        isFieldEnabled(activeFormat, "nature_of_business")) &&
      !formData.businessNature
    ) {
      errors.businessNature = "Select Nature of Business";
    }
    if (isFieldEnabled(activeFormat, "state") && !formData.state) {
      errors.state = "Select State";
    }
    if (isFieldEnabled(activeFormat, "city") && !formData.city?.trim()) {
      errors.city = "Select or Enter City";
    }

    if (
      activeFormat?.visitor_format_child &&
      Array.isArray(activeFormat.visitor_format_child)
    ) {
      activeFormat.visitor_format_child
        .filter((child) => child && isChildRequired(child))
        .forEach((child) => {
          const fieldKey = `child_${child.id}`;
          const val = formData[fieldKey];
          const isSelectType =
            child.type === "select" ||
            child.type === "dropdown" ||
            child.type === "radio" ||
            child.type === "checkbox" ||
            child.type === "file" ||
            child.type === "image" ||
            child.type === "date" ||
            child.type === "datetime";
          const actionText = isSelectType ? "Select" : "Enter";

          if (!val || (typeof val === "string" && !val.trim())) {
            errors[fieldKey] = `${actionText} ${child.label}`;
          } else if (Array.isArray(val) && val.length === 0) {
            errors[fieldKey] = `${actionText} ${child.label}`;
          }
        });
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    const errorList = Object.values(errors);
    if (errorList.length > 0) {
      Notification("error", "Missing Info!", errorList[0], 3);
      return;
    }

    // ---------- Build FormData as per API (Postman) ----------
    const fd = new FormData();

    // first_name & last_name
    let fName = formData.firstName?.trim() || "";
    let lName = formData.lastName?.trim() || "";

    if (!fName && !lName && formData.fullName?.trim()) {
      const parts = formData.fullName.trim().split(" ");
      fName = parts[0] || "";
      lName = parts.slice(1).join(" ") || "";
    }

    if (fName) fd.append("first_name", fName);
    if (lName) fd.append("last_name", lName);

    // email
    if (formData.email?.trim()) fd.append("email", formData.email.trim());

    // contact
    const contactVal = (
      formData.mobile ||
      formData.contact ||
      formData.whatsapp ||
      ""
    ).trim();
    if (contactVal) fd.append("contact", contactVal);

    // company
    if (formData.companyName?.trim()) {
      fd.append("company", formData.companyName.trim());
    }

    // designation
    if (formData.designation?.trim()) {
      fd.append("designation", formData.designation.trim());
    }

    // title (Mr / Ms ...) - only if available in formData
    if (formData.title) fd.append("title", formData.title);

    // country (id)
    const selectedCountry = Array.isArray(get_country_data)
      ? get_country_data.find(
        (c) =>
          c?.name === formData.country ||
          String(c?.id) === String(formData.country)
      )
      : null;
    if (selectedCountry?.id || formData.country) {
      fd.append("country", selectedCountry?.id || formData.country);
    }

    // country_code
    if (formData.countryCode?.trim()) {
      fd.append("country_code", formData.countryCode.trim());
    }

    // state (id)
    if (formData.state) fd.append("state", formData.state);

    // city (id / name)
    const selectedCity = Array.isArray(get_city_data)
      ? get_city_data.find((ct) => ct?.name === formData.city)
      : null;
    if (selectedCity?.id || formData.city) {
      fd.append("city", selectedCity?.id || formData.city);
    }

    // business_nature
    if (
      isFieldEnabled(activeFormat, "business_nature") ||
      isFieldEnabled(activeFormat, "nature_of_business")
    ) {
      if (formData.businessNature) {
        fd.append("business_nature_options[]", formData.businessNature);
      }
    }

    // product_category_option[]
    if (selectedProductCategories && selectedProductCategories.length > 0) {
      selectedProductCategories.forEach((cat) => {
        fd.append("product_category_option[]", cat);
      });
    }

    // child_answers[id]
    if (
      activeFormat?.visitor_format_child &&
      Array.isArray(activeFormat.visitor_format_child)
    ) {
      activeFormat.visitor_format_child.filter(Boolean).forEach((child) => {
        const val = formData[`child_${child.id}`];
        if (val === undefined || val === null || val === "") return;

        if (Array.isArray(val)) {
          val.forEach((v) => fd.append(`child_answers[${child.id}][]`, v));
        } else {
          fd.append(`child_answers[${child.id}]`, val);
        }
      });
    }

    // Wait for API result -> clear the form only on success
    const isSuccess = await PostInternationalForm(fd);
    if (isSuccess) {
      resetForm();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const isIntlData =
    !register_detail_expo_data?.registration_category ||
    register_detail_expo_data?.registration_category?.name
      ?.toLowerCase()
      .includes("international") ||
    register_detail_expo_data?.registration_category?.tag
      ?.toLowerCase()
      .includes("international") ||
    register_detail_expo_data?.registration_category?.name
      ?.toLowerCase()
      .includes("overseas") ||
    register_detail_expo_data?.registration_category?.name
      ?.toLowerCase()
      .includes("foreign");

  if (
    register_detail_expo_loading ||
    register_form_loading ||
    !register_detail_expo_data ||
    Object.keys(register_detail_expo_data).length === 0 ||
    !isIntlData
  ) {
    return (
      <Loader fullScreen={true} text="Loading International Visitors..." />
    );
  }

  const showTitle = isFieldEnabled(activeFormat, "title");
  const showFirstName =
    isFieldEnabled(activeFormat, "first_name") ||
    isFieldEnabled(activeFormat, "firstname");
  const showLastName =
    isFieldEnabled(activeFormat, "last_name") ||
    isFieldEnabled(activeFormat, "lastname");
  const showFullName = isFieldEnabled(activeFormat, "full_name");
  const showContact =
    isFieldEnabled(activeFormat, "contact") ||
    isFieldEnabled(activeFormat, "contact_no") ||
    isFieldEnabled(activeFormat, "mobile") ||
    isFieldEnabled(activeFormat, "whatsapp");
  const showEmail = isFieldEnabled(activeFormat, "email");
  const showCompany =
    isFieldEnabled(activeFormat, "company") ||
    isFieldEnabled(activeFormat, "company_name");
  const showDesignation =
    isFieldEnabled(activeFormat, "designation") ||
    isFieldEnabled(activeFormat, "designation_name");
  const showProductCategory = isFieldEnabled(activeFormat, "product_category");
  const showBusinessNature =
    isFieldEnabled(activeFormat, "business_nature") ||
    isFieldEnabled(activeFormat, "nature_of_business");
  const showBusinessSector = isFieldEnabled(activeFormat, "business_sector");
  const showCountryCode = isFieldEnabled(activeFormat, "country_code");
  const showCountry = isFieldEnabled(activeFormat, "country");
  const showState = isFieldEnabled(activeFormat, "state");
  const showCity = isFieldEnabled(activeFormat, "city");

  const titleOpts = getCleanOptions(
    activeFormat.title_option || activeFormat.title_options
  );

  const businessSectorOpts = getCleanOptions(
    activeFormat.business_sector_option ||
    activeFormat.business_sector_options
  );
  const businessNatureOpts = getCleanOptions(
    activeFormat.business_nature_option ||
    activeFormat.business_nature_options ||
    activeFormat.nature_of_business_option ||
    activeFormat.nature_of_business_options
  );
  const productCategoryOpts = getCleanOptions(
    activeFormat.product_category_option ||
    activeFormat.product_category_options
  );

  return (
    <div className="intl-vis-page">
      <Helmet>
        <title>International Visitors Registration | Bharat Buildcon</title>
      </Helmet>
      {/* Background Decorative Mesh Shapes */}
      <div className="intl-bg-glow intl-bg-glow--saffron" />
      <div className="intl-bg-glow intl-bg-glow--blue" />

      {/* ---------------- BREADCRUMB & HERO BANNER ---------------- */}
      <section className="intl-hero-section">
        <div className="intl-container">
          {/* Dynamic Hero Banner Frame */}
          <div className="intl-hero-banner-card">
            <div className="intl-hero-bg-media">
              <img
                src={
                  register_detail_expo_data?.registration_detail
                    ?.image_full_path ||
                  "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1600&q=80"
                }
                alt="Bharat Buildcon Expo"
                className="intl-hero-img"
              />
              <div className="intl-hero-overlay" />
              <div className="intl-hero-scan-line" />
              <div className="intl-hero-grid-pattern" />
            </div>

            <div className="intl-hero-content">
              <div className="intl-ribbon-badge">
                <span className="intl-ribbon-spark" />
                <FaGlobe className="intl-ribbon-icon" />
                <span>
                  {register_detail_expo_data?.registration_detail
                    ?.reg_detail_sub_heading ||
                    "GLOBAL DELEGATE REGISTRATION 2026"}
                </span>
              </div>

              <h1 className="intl-hero-title">
                {register_detail_expo_data?.registration_detail
                  ?.reg_detail_heading ? (
                  (() => {
                    const words =
                      register_detail_expo_data?.registration_detail?.reg_detail_heading
                        .trim()
                        .split(" ");
                    if (words.length === 1) {
                      return (
                        <span className="intl-shimmer-text">{words[0]}</span>
                      );
                    }
                    const mainText = words.slice(0, -1).join(" ");
                    const lastWord = words[words.length - 1];
                    return (
                      <>
                        {mainText}{" "}
                        <span className="intl-shimmer-text">{lastWord}</span>
                      </>
                    );
                  })()
                ) : (
                  <>
                    International{" "}
                    <span className="intl-shimmer-text">
                      Visitors Registration
                    </span>
                  </>
                )}
              </h1>
            </div>

            {/* Quick Info Feature Strip inside Card */}
            <div className="intl-info-strip">
              {Number(
                register_detail_expo_data?.registration_category
                  ?.registration_free
              ) === 1 && (
                  <>
                    <div className="intl-info-item">
                      <div className="intl-info-icon-box">
                        <FaTicket />
                      </div>
                      <div className="intl-info-text">
                        <span className="intl-info-label">ENTRY PASS</span>
                        <span className="intl-info-val">Free Entry Pass</span>
                      </div>
                    </div>

                    <div className="intl-info-divider" />
                  </>
                )}



              <div className="intl-info-item">
                <div className="intl-info-icon-box">
                  <FaCalendarDays />
                </div>
                <div className="intl-info-text">
                  <span className="intl-info-label">DATES</span>
                  <span className="intl-info-val">
                    {formattedDates || "18 – 21 June 2026"}
                  </span>
                </div>
              </div>

              <div className="intl-info-divider" />

              <div className="intl-info-item">
                <div className="intl-info-icon-box">
                  <FaLocationDot />
                </div>
                <div className="intl-info-text">
                  <span className="intl-info-label">VENUE</span>
                  <span className="intl-info-val">
                    {register_detail_expo_data?.venue_name ||
                      "Yashobhoomi, New Delhi"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- MAIN FORM SECTION ---------------- */}
      <section className="intl-main-section">
        <div className="intl-container">
          <div className="intl-layout-grid">
            <div className="intl-main-col">
              <div className="intl-card intl-form-card intl-form-elevated">
                <div className="intl-card-header">
                  <div className="intl-header-top">
                    <span className="intl-subhead">
                      TELL US A LITTLE MORE ABOUT YOU
                    </span>
                  </div>
                  <h2 className="intl-card-title">
                    International Delegate Registration
                  </h2>
                  <p className="intl-card-desc">
                    Please fill out your details below to receive your official
                    international delegate badge &amp; visa invitation letter.
                  </p>
                </div>

                <form
                  key={formKey}
                  onSubmit={handleSubmit}
                  className="intl-form"
                >
                  {/* Title * */}
                  {showTitle && (
                    <div
                      className="intl-field-group intl-field-wide"
                      style={{ marginBottom: "22px" }}
                    >
                      <label className="intl-label">
                        Title <span className="intl-required">*</span>
                      </label>
                      <div className="intl-title-radio-group">
                        {titleOpts.map((t) => (
                          <label
                            key={t}
                            className={`intl-radio-pill ${formData.title === t
                              ? "intl-radio-pill-active"
                              : ""
                              }`}
                          >
                            <input
                              type="radio"
                              name="title"
                              value={t}
                              checked={formData.title === t}
                              onChange={handleInputChange}
                              className="intl-radio-input"
                            />
                            <span>{t}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="intl-form-grid">
                    {/* First Name */}
                    {showFirstName && (
                      <div className="intl-field-group">
                        <label htmlFor="firstName" className="intl-label">
                          First Name (As per passport){" "}
                          <span className="intl-required">*</span>
                        </label>
                        <div className="intl-input-wrapper">
                          <FaUser className="intl-field-icon" />
                          <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName || ""}
                            onChange={handleInputChange}
                            placeholder="Enter first name"
                            className="intl-input"
                          />
                        </div>
                      </div>
                    )}

                    {/* Last Name */}
                    {showLastName && (
                      <div className="intl-field-group">
                        <label htmlFor="lastName" className="intl-label">
                          Last Name (As per passport){" "}
                          <span className="intl-required">*</span>
                        </label>
                        <div className="intl-input-wrapper">
                          <FaUser className="intl-field-icon" />
                          <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName || ""}
                            onChange={handleInputChange}
                            placeholder="Enter last name"
                            className="intl-input"
                          />
                        </div>
                      </div>
                    )}

                    {/* Full Name */}
                    {showFullName && (
                      <div className="intl-field-group">
                        <label htmlFor="fullName" className="intl-label">
                          Full Name <span className="intl-required">*</span>
                        </label>
                        <div className="intl-input-wrapper">
                          <FaUser className="intl-field-icon" />
                          <input
                            type="text"
                            id="fullName"
                            name="fullName"
                            value={formData.fullName || ""}
                            onChange={handleInputChange}
                            placeholder="Enter full name"
                            className="intl-input"
                          />
                        </div>
                      </div>
                    )}



                    {/* Participant Mobile / Whatsapp with integrated Country Code inside 1 single input box */}
                    {(showContact || showCountryCode) && (
                      <div className="intl-field-group">
                        <label htmlFor="mobile" className="intl-label">
                          Participant Mobile/Whatsapp number
                          <span className="intl-required">*</span>
                        </label>
                        <div className="intl-unified-phone-container">
                          {/* Country Code embedded section with real Flag image icon and API phonecode dropdown */}
                          {showCountryCode && (
                            <>
                              <div className="intl-country-code-embed">
                                {selectedSortName ? (
                                  <img
                                    src={`https://flagcdn.com/w40/${selectedSortName}.png`}
                                    alt={selectedSortName}
                                    className="intl-embed-flag-img"
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <FaGlobe className="intl-embed-icon" />
                                )}
                                <span className="intl-embed-code-text">
                                  {formData.countryCode || "+91"}
                                </span>
                                <FaChevronDown className="intl-embed-chevron" />
                                <select
                                  id="countryCode"
                                  name="countryCode"
                                  value={formData.countryCode || "+91"}
                                  onChange={handleInputChange}
                                  className="intl-embed-cc-select"
                                  disabled={get_country_loading}
                                >
                                  {Array.isArray(get_country_data) &&
                                    get_country_data.map((c, idx) => {
                                      const rawCode =
                                        c?.phonecode ||
                                        c?.phone_code ||
                                        c?.country_code ||
                                        c?.isd_code ||
                                        c?.code ||
                                        "";
                                      if (!rawCode) return null;
                                      const formattedCode = String(rawCode).startsWith("+")
                                        ? String(rawCode)
                                        : `+${rawCode}`;
                                      const countryName = c?.name || c?.country_name || "";
                                      const displayLabel = countryName
                                        ? `${formattedCode} (${countryName})`
                                        : formattedCode;
                                      return (
                                        <option key={c?.id || idx} value={formattedCode}>
                                          {displayLabel}
                                        </option>
                                      );
                                    })}
                                </select>
                              </div>

                              {/* Vertical Divider */}
                              <div className="intl-phone-divider" />
                            </>
                          )}

                          {/* Mobile Number embedded section */}
                          <div className="intl-mobile-number-embed">
                            <FaWhatsapp className="intl-embed-icon intl-whatsapp-icon" />
                            <input
                              type="tel"
                              id="mobile"
                              name="mobile"
                              value={formData.mobile || formData.contact || ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                setFormData((prev) => ({
                                  ...prev,
                                  mobile: val,
                                  contact: val,
                                }));
                              }}
                              placeholder="e.g. 98765 43210"
                              className="intl-embed-mobile-input"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Participant Email */}
                    {showEmail && (
                      <div className="intl-field-group">
                        <label htmlFor="email" className="intl-label">
                          Participant Email{" "}
                          <span className="intl-required">*</span>
                        </label>
                        <div className="intl-input-wrapper">
                          <FaEnvelope className="intl-field-icon" />
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email || ""}
                            onChange={handleInputChange}
                            placeholder="e.g. email@example.com"
                            className="intl-input"
                          />
                        </div>
                      </div>
                    )}

                    {/* Company Name */}
                    {showCompany && (
                      <div className="intl-field-group">
                        <label htmlFor="companyName" className="intl-label">
                          Company Name <span className="intl-required">*</span>
                        </label>
                        <div className="intl-input-wrapper">
                          <FaBuilding className="intl-field-icon" />
                          <input
                            type="text"
                            id="companyName"
                            name="companyName"
                            value={formData.companyName || ""}
                            onChange={handleInputChange}
                            placeholder="Enter company name"
                            className="intl-input"
                          />
                        </div>
                      </div>
                    )}

                    {/* Designation of the Representative */}
                    {showDesignation && (
                      <div className="intl-field-group">
                        <label htmlFor="designation" className="intl-label">
                          Designation of the Representative{" "}
                          <span className="intl-required">*</span>
                        </label>
                        <div className="intl-input-wrapper">
                          <FaBriefcase className="intl-field-icon" />
                          <input
                            type="text"
                            id="designation"
                            name="designation"
                            value={formData.designation || ""}
                            onChange={handleInputChange}
                            placeholder="e.g. Director / Manager"
                            className="intl-input"
                          />
                        </div>
                      </div>
                    )}

                    {/* Nature of Business Dropdown */}
                    {showBusinessNature && (
                      <div className="intl-field-group">
                        <label htmlFor="businessNature" className="intl-label">
                          Nature of Business <span className="intl-required">*</span>
                        </label>
                        <div className="intl-input-wrapper">
                          <FaUser className="intl-field-icon" />
                          <select
                            id="businessNature"
                            name="businessNature"
                            value={formData.businessNature || ""}
                            onChange={handleInputChange}
                            className="intl-select"
                          >
                            <option value="">Select Nature of Business</option>
                            {businessNatureOpts.map((nat, idx) => (
                              <option key={idx} value={nat}>
                                {nat}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {/* Business Sector Dropdown */}
                    {showBusinessSector && (
                      <div className="intl-field-group">
                        <label htmlFor="businessSector" className="intl-label">
                          Main Business Sector <span className="intl-required">*</span>
                        </label>
                        <div className="intl-input-wrapper">
                          <FaBriefcase className="intl-field-icon" />
                          <select
                            id="businessSector"
                            name="businessSector"
                            value={formData.businessSector || ""}
                            onChange={handleInputChange}
                            className="intl-select"
                          >
                            <option value="">Select Main Business Sector</option>
                            {businessSectorOpts.map((sector, idx) => (
                              <option key={idx} value={sector}>
                                {sector}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {/* Country */}
                    {showCountry && (
                      <div className="intl-field-group">
                        <label htmlFor="country" className="intl-label">
                          Country <span className="intl-required">*</span>
                        </label>
                        <div className="intl-input-wrapper">
                          <FaGlobe className="intl-field-icon" />
                          <select
                            id="country"
                            name="country"
                            value={formData.country || ""}
                            onChange={handleInputChange}
                            disabled={get_country_loading}
                            className="intl-select"
                          >
                            <option value="">
                              {get_country_loading
                                ? "Loading Countries..."
                                : "Select Country"}
                            </option>
                            {get_country_data &&
                              get_country_data.map((c) => (
                                <option
                                  key={c.id || c.country_name || c.name}
                                  value={c.id || c.country_name || c.name}
                                >
                                  {c.name || c.country_name || c.title}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {/* State Dropdown */}
                    {showState && (
                      <div className="intl-field-group">
                        <label htmlFor="state" className="intl-label">
                          State <span className="intl-required">*</span>
                        </label>
                        <div className="intl-input-wrapper">
                          <FaLocationDot className="intl-field-icon" />
                          <select
                            id="state"
                            name="state"
                            value={formData.state || ""}
                            onChange={handleInputChange}
                            disabled={get_state_loading}
                            className="intl-select"
                          >
                            <option value="">
                              {get_state_loading
                                ? "Loading States..."
                                : "Select State"}
                            </option>
                            {get_state_data &&
                              get_state_data.map((st) => (
                                <option key={st.id || st.name} value={st.id}>
                                  {st.name}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {/* City */}
                    {showCity && (
                      <div className="intl-field-group">
                        <label htmlFor="city" className="intl-label">
                          City <span className="intl-required">*</span>
                        </label>
                        <div className="intl-input-wrapper">
                          <FaCity className="intl-field-icon" />
                          {showState ? (
                            <select
                              id="city"
                              name="city"
                              value={formData.city || ""}
                              onChange={handleInputChange}
                              disabled={!formData.state || get_city_loading}
                              className="intl-select"
                            >
                              <option value="">
                                {!formData.state
                                  ? "Select State First"
                                  : get_city_loading
                                    ? "Loading Cities..."
                                    : "Select City"}
                              </option>
                              {formData.state &&
                                get_city_data &&
                                get_city_data.map((ct) => (
                                  <option key={ct.id || ct.name} value={ct.name}>
                                    {ct.name}
                                  </option>
                                ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              id="city"
                              name="city"
                              value={formData.city || ""}
                              onChange={handleInputChange}
                              placeholder="Enter city"
                              className="intl-input"
                            />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Product Category Checkboxes */}
                    {showProductCategory && (
                      <div className="intl-field-group intl-field-group--full">
                        <label className="intl-label">
                          Product Category <span className="intl-required">*</span>
                        </label>
                        <div className="intl-category-checkbox-grid">
                          {productCategoryOpts.map((category, index) => {
                            const isChecked = selectedProductCategories.includes(category);
                            return (
                              <label
                                key={index}
                                className={`intl-category-checkbox-item ${isChecked ? "selected" : ""}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleCategoryToggle(category)}
                                  className="intl-checkbox-input"
                                />
                                <span className="intl-checkbox-label">{category}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* DYNAMIC CHILD FIELDS (from visitor_format_child) */}
                    {activeFormat?.visitor_format_child &&
                      Array.isArray(activeFormat.visitor_format_child) &&
                      activeFormat.visitor_format_child
                        .filter(Boolean)
                        .map((child) => {
                          const fieldKey = `child_${child.id}`;
                          const fieldOptions = getChildOptions(child);
                          const isReq = isChildRequired(child);
                          const fieldIcon = getFieldIcon(child.label);

                          // Select Dropdown Type
                          if (
                            child.type === "select" ||
                            child.type === "dropdown"
                          ) {
                            return (
                              <div className="intl-field-group" key={child.id}>
                                <label
                                  htmlFor={fieldKey}
                                  className="intl-label"
                                >
                                  {child.label}
                                  {isReq && (
                                    <span className="intl-required">*</span>
                                  )}
                                </label>
                                <div className="intl-input-wrapper">
                                  {fieldIcon}
                                  <select
                                    id={fieldKey}
                                    name={fieldKey}
                                    value={formData[fieldKey] || ""}
                                    onChange={handleInputChange}
                                    className="intl-select"
                                  >
                                    <option value="">Select {child.label}</option>
                                    {fieldOptions.map((opt, idx) => (
                                      <option key={idx} value={opt}>
                                        {opt}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            );
                          }

                          // Textarea Type
                          if (child.type === "textarea") {
                            return (
                              <div
                                className="intl-field-group intl-field-wide"
                                key={child.id}
                              >
                                <label
                                  htmlFor={fieldKey}
                                  className="intl-label"
                                >
                                  {child.label}
                                  {isReq && (
                                    <span className="intl-required">*</span>
                                  )}
                                </label>
                                <div className="intl-input-wrapper">
                                  <FaFileContract
                                    className="intl-field-icon"
                                    style={{ top: "18px" }}
                                  />
                                  <textarea
                                    id={fieldKey}
                                    name={fieldKey}
                                    value={formData[fieldKey] || ""}
                                    onChange={handleInputChange}
                                    placeholder={`Enter ${child.label.toLowerCase()}`}
                                    className="intl-input intl-textarea"
                                    rows={3}
                                  />
                                </div>
                              </div>
                            );
                          }

                          // Radio Type
                          if (child.type === "radio") {
                            return (
                              <div
                                className={`intl-field-group ${fieldOptions.length > 3 ? "intl-field-wide" : ""}`}
                                key={child.id}
                              >
                                <label className="intl-label">
                                  {child.label}
                                  {isReq && (
                                    <span className="intl-required">*</span>
                                  )}
                                </label>
                                <div className="intl-radio-group">
                                  {fieldOptions.map((opt, idx) => (
                                    <label key={idx} className="intl-radio-item">
                                      <input
                                        type="radio"
                                        name={fieldKey}
                                        value={opt}
                                        checked={formData[fieldKey] === opt}
                                        onChange={handleInputChange}
                                      />
                                      <span>{opt}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            );
                          }

                          // Checkbox Type
                          if (child.type === "checkbox") {
                            return (
                              <div
                                className={`intl-field-group ${fieldOptions.length > 3 ? "intl-field-wide" : ""}`}
                                key={child.id}
                              >
                                <label className="intl-label">
                                  {child.label}
                                  {isReq && (
                                    <span className="intl-required">*</span>
                                  )}
                                </label>
                                <div className="intl-checkbox-group">
                                  {fieldOptions.map((opt, idx) => {
                                    const selectedVals =
                                      formData[fieldKey] || [];
                                    const isChecked =
                                      Array.isArray(selectedVals) &&
                                      selectedVals.includes(opt);
                                    return (
                                      <label
                                        key={idx}
                                        className="intl-checkbox-item"
                                      >
                                        <input
                                          type="checkbox"
                                          name={fieldKey}
                                          value={opt}
                                          checked={isChecked}
                                          onChange={(e) => {
                                            const curr = Array.isArray(
                                              formData[fieldKey]
                                            )
                                              ? formData[fieldKey]
                                              : [];
                                            const next = e.target.checked
                                              ? [...curr, opt]
                                              : curr.filter((v) => v !== opt);
                                            setFormData((prev) => ({
                                              ...prev,
                                              [fieldKey]: next,
                                            }));
                                          }}
                                        />
                                        <span>{opt}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          }

                          // File / Image Input Type
                          if (child.type === "file" || child.type === "image") {
                            return (
                              <div
                                className="intl-field-group"
                                key={child.id}
                              >
                                <label
                                  htmlFor={fieldKey}
                                  className="intl-label"
                                >
                                  {child.label}
                                  {isReq && (
                                    <span className="intl-required">*</span>
                                  )}
                                </label>
                                <div className="intl-file-input-wrapper">
                                  <input
                                    type="file"
                                    id={fieldKey}
                                    name={fieldKey}
                                    accept="image/*,.pdf"
                                    onChange={(e) => {
                                      const selectedFile = e.target.files[0];
                                      setFormData((prev) => ({
                                        ...prev,
                                        [fieldKey]: selectedFile || null,
                                      }));
                                    }}
                                    className="intl-file-native-input"
                                  />
                                  <button
                                    type="button"
                                    className="intl-btn-choose-file"
                                  >
                                    Choose File
                                  </button>
                                  <span className="intl-file-chosen-text">
                                    {formData[fieldKey]?.name
                                      ? formData[fieldKey].name
                                      : "No file chosen"}
                                  </span>
                                </div>
                              </div>
                            );
                          }

                          // Date Input Type
                          if (child.type === "date" || child.type === "datetime") {
                            return (
                              <div className="intl-field-group" key={child.id}>
                                <label htmlFor={fieldKey} className="intl-label">
                                  {child.label}
                                  {isReq && <span className="intl-required">*</span>}
                                </label>
                                <div className="intl-input-wrapper">
                                  <FaCalendarDays className="intl-field-icon" />
                                  <input
                                    type="date"
                                    id={fieldKey}
                                    name={fieldKey}
                                    value={formData[fieldKey] || ""}
                                    onChange={handleInputChange}
                                    className="intl-input"
                                  />
                                </div>
                              </div>
                            );
                          }

                          // Standard Text / Default Input Type
                          const isWide = child.label
                            ?.toLowerCase()
                            .includes("address");
                          return (
                            <div
                              className={`intl-field-group ${isWide ? "intl-field-wide" : ""
                                }`}
                              key={child.id}
                            >
                              <label
                                htmlFor={fieldKey}
                                className="intl-label"
                              >
                                {child.label}
                                {isReq && (
                                  <span className="intl-required">*</span>
                                )}
                              </label>
                              <div className="intl-input-wrapper">
                                {fieldIcon}
                                <input
                                  type={
                                    child.type === "email"
                                      ? "email"
                                      : child.type === "number"
                                        ? "number"
                                        : "text"
                                  }
                                  id={fieldKey}
                                  name={fieldKey}
                                  value={formData[fieldKey] || ""}
                                  onChange={handleInputChange}
                                  placeholder={`Enter ${child.label.toLowerCase()}`}
                                  className="intl-input"
                                />
                              </div>
                            </div>
                          );
                        })}
                  </div>

                  {/* Submit Button */}
                  <div className="intl-form-actions">
                    <button
                      type="submit"
                      disabled={submit_international_visitor_loading}
                      className="intl-btn-submit intl-btn-animated"
                    >
                      <span className="intl-btn-shine" />
                      {submit_international_visitor_loading ? (
                        <span className="intl-btn-loading">
                          <span className="intl-spinner" /> Submitting...
                        </span>
                      ) : (
                        <>
                          <span>SUBMIT</span>
                          <FaArrowRight className="intl-btn-arrow" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Right Column: Information & Perks Sidebar */}
            <div className="intl-sidebar-col">
              {/* Event Perks Card */}
              {register_detail_expo_data?.registration_detail?.why_registers &&
                register_detail_expo_data?.registration_detail?.why_registers
                  .length > 0 && (
                  <div className="intl-card intl-sidebar-card">
                    <div className="intl-sb-header">
                      <FaGem className="intl-sb-icon" />
                      <h3>International Delegate Perks</h3>
                    </div>

                    <div className="intl-perks-list">
                      {register_detail_expo_data?.registration_detail?.why_registers
                        .filter((item) => item.name || item.description)
                        .map((item, index) => (
                          <div
                            className="intl-perk-item"
                            key={item.id || index}
                          >
                            {item.icon_name && item.icon_name.trim() && (
                              <div className="intl-perk-bullet">
                                <DynamicIcon
                                  name={item.icon_name}
                                  fallback={null}
                                />
                              </div>
                            )}
                            <div>
                              {item.name && <h4>{item.name}</h4>}
                              {item.description && <p>{item.description}</p>}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

              {/* Support & Contact Card */}
              <div className="intl-card intl-sidebar-card intl-help-card">
                <div className="intl-sb-header">
                  <FaHeadset className="intl-sb-icon intl-sb-icon-orange" />
                  <h3>Registration Helpdesk</h3>
                </div>
                {(register_detail_expo_data?.registration_detail
                  ?.registration_heplpdesk_description ||
                  register_detail_expo_data?.registration_heplpdesk_description) && (
                    <p className="intl-help-text">
                      {register_detail_expo_data?.registration_detail
                        ?.registration_heplpdesk_description ||
                        register_detail_expo_data?.registration_heplpdesk_description}
                    </p>
                  )}

                {register_detail_expo_data?.registration_detail
                  ?.registration_helpdesks &&
                  register_detail_expo_data?.registration_detail
                    ?.registration_helpdesks.length > 0 && (
                    <div className="intl-contact-methods">
                      {register_detail_expo_data?.registration_detail?.registration_helpdesks
                        .filter((item) => item.title || item.detail)
                        .map((item, index) => (
                          <div
                            className="intl-contact-row"
                            key={item.id || index}
                          >
                            {(item.icon_name || item.icon) &&
                              (item.icon_name || item.icon).trim() && (
                                <div className="intl-c-icon">
                                  <DynamicIcon
                                    name={item.icon_name || item.icon}
                                    fallback={null}
                                  />
                                </div>
                              )}
                            <div>
                              {item.title && (
                                <span className="intl-c-lbl">{item.title}</span>
                              )}
                              {item.detail && (
                                <span className="intl-c-val">
                                  {item.detail}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
              </div>

              {/* Security Banner */}
              <div className="intl-sec-box">
                <FaShieldHalved className="intl-sec-icon" />
                <span>
                  Your details are 100% confidential and secure with Bharat
                  Buildcon.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InternationalVisitors;

